import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from '../button';
import Loading from '../loading';
import zhCN from '../locale/zh-cn';
import { func, obj, guid } from '../util';

const { makeChain } = func;
const { pickAttrs } = obj;
const noop = () => {};

export default class Inner extends Component {
  static propTypes = {
    prefix: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    title: PropTypes.node,
    children: PropTypes.node,
    footer: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    footerAlign: PropTypes.oneOf(['left', 'center', 'right']),
    footerActions: PropTypes.array,
    onOk: PropTypes.func,
    onCancel: PropTypes.func,
    okProps: PropTypes.object,
    cancelProps: PropTypes.object,
    closeable: PropTypes.bool,
    onClose: PropTypes.func,
    locale: PropTypes.object,
    role: PropTypes.string,
    rtl: PropTypes.bool,
    // set value for a fixed height dialog. Passing a value will absolutely position the footer to the bottom.
    height: PropTypes.string,
    /**
     * 没有内容区的padding
     */
    noBodyPadding: PropTypes.bool,
    /**
     * 是否处于加载状态
     */
    loading: PropTypes.bool,
    /**
     * 加载提示
     */
    loadingTip: PropTypes.node,
  };

  static defaultProps = {
    prefix: 'next-',
    size: 'medium',
    footerAlign: 'right',
    footerActions: ['ok', 'cancel'],
    onOk: noop,
    onCancel: noop,
    okProps: {},
    cancelProps: {},
    closeable: true,
    onClose: noop,
    locale: zhCN.Dialog,
    role: 'dialog',
    noBodyPadding: false,
    loading: false,
  };

  getNode = (name, ref) => {
    this[name] = ref;
  }

  renderHeader = () => {
    const { prefix, title } = this.props;
    const closeLink = this.renderCloseLink();
    if (title) {
      this.titleId = guid('dialog-title-');
      return (
        <div
          className={`${prefix}dialog-header`}
          id={this.titleId}
          ref={this.getNode.bind(this, 'headerNode')}
          role="heading"
          aria-level="1"
        >
          {title}
          {closeLink}
        </div>
      );
    }
    return null;
  }

  renderBody = () => {
    const { prefix, children, noBodyPadding } = this.props;
    if (children) {
      return (
        <div
          className={cx({
            [`${prefix}dialog-body`]: true,
            [`${prefix}dialog-body-no-padding`]: noBodyPadding,
          })}
          ref={this.getNode.bind(this, 'bodyNode')}
        >
          {children}
        </div>
      );
    }
    return null;
  }

  renderFooter = () => {
    const { prefix, footer, footerAlign, footerActions, locale, height } = this.props;

    if (footer === false) {
      return null;
    }

    const newClassName = cx({
      [`${prefix}dialog-footer`]: true,
      [`${prefix}align-${footerAlign}`]: true,
      [`${prefix}dialog-footer-fixed-height`]: !!height,
    });
    const footerContent =
      footer === true || !footer
        ? footerActions.map(action => {
          const btnProps = this.props[`${action}Props`];
          const newBtnProps = {
            ...btnProps,
            prefix,
            className: cx(`${prefix}dialog-btn`, btnProps.className),
            onClick: makeChain(
              this.props[`on${action[0].toUpperCase() + action.slice(1)}`],
              btnProps.onClick,
            ),
            children: btnProps.children || locale[action],
          };
          if (action === 'ok') {
            newBtnProps.type = 'primary';
          }

          return <Button key={action} {...newBtnProps} />;
        })
        : footer;

    return (
      <div className={newClassName} ref={this.getNode.bind(this, 'footerNode')}>
        {footerContent}
      </div>
    );
  }

  renderCloseLink = () => {
    const { prefix, closeable, onClose, locale } = this.props;

    if (closeable) {
      return (
        <a
          role="button"
          aria-label={locale.close}
          href="javascript:;"
          className={`${prefix}dialog-close`}
          onClick={onClose}
        >
          <Button.Icon
            type="primary"
            color="gray"
            className={`${prefix}dialog-close-icon`}
            name="remove"
          />
        </a>
      );
    }

    return null;
  }

  render() {
    const {
      prefix,
      className,
      size,
      closeable,
      title,
      role,
      rtl,
      height,
      loading,
      loadingTip,
      ...others
    } = this.props;
    const newClassName = cx({
      [`${prefix}dialog`]: true,
      [`${prefix}dialog-${size}`]: true,
      [`${prefix}closeable`]: closeable,
      [className]: !!className,
    });

    const header = this.renderHeader();
    const body = this.renderBody();
    const footer = this.renderFooter();

    const ariaProps = {
      role,
      'aria-modal': 'true',
    };
    if (title) {
      ariaProps['aria-labelledby'] = this.titleId;
    }

    others.style = Object.assign({}, others.style, { height });

    return (
      <div {...ariaProps} className={newClassName} {...pickAttrs(others)} dir={rtl ? 'rtl' : undefined}>
        <Loading visible={loading} tip={loadingTip} inline={false}>
          {header}
          {body}
          {footer}
        </Loading>
      </div>
    );
  }
}

